import React, { useState } from 'react';
import type { MfeConfig, MfeOverride } from './DeveloperWorkbench.types';

interface OverridesTabProps {
  configs: MfeConfig[];
  overrides: Record<string, MfeOverride>;
  onInputChange: (key: string, field: 'scriptUrl' | 'cssUrl', value: string) => void;
}

export const OverridesTab: React.FC<OverridesTabProps> = ({ configs, overrides, onInputChange }) => {
  const [selectedMfeKey, setSelectedMfeKey] = useState<string>(configs[0]?.key || '');

  const mfe = configs.find((c) => c.key === selectedMfeKey);
  if (!mfe) return null;

  const override = overrides[mfe.key];
  const isOverridden = !!override;

  return (
    <div className="flex flex-col gap-4">
      <h4 className="text-xs font-extrabold uppercase tracking-wider text-text-muted flex items-center gap-2">
        <i className="fa-solid fa-network-wired text-primary-accent"></i> Micro Frontend Overrides
      </h4>
      
      {/* Select MFE Dropdown */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="mfe-selector" className="text-xs font-bold text-text-secondary">
          Select Micro Frontend to Configure
        </label>
        <div className="relative">
          <select
            id="mfe-selector"
            value={selectedMfeKey}
            onChange={(e) => setSelectedMfeKey(e.target.value)}
            className="bg-bg-surface border border-border-color rounded-xl pl-4 pr-10 py-2.5 text-xs md:text-sm text-text-primary w-full appearance-none outline-none focus:border-primary-accent transition-colors cursor-pointer"
          >
            {configs.map((mfe) => (
              <option key={mfe.key} value={mfe.key} className="bg-bg-surface text-text-primary">
                {mfe.name}
              </option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3.5 text-text-secondary">
            <i className="fa-solid fa-chevron-down text-xs"></i>
          </div>
        </div>
      </div>

      {/* Selected MFE Details */}
      <div className="p-4 rounded-2xl bg-bg-surface border border-border-color flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <span className="font-bold text-sm text-text-primary">{mfe.name}</span>
          <span className={`px-2.5 py-0.5 rounded-lg text-[10px] font-extrabold flex items-center gap-1.5 border ${
            isOverridden 
              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
              : 'bg-white/5 text-text-secondary border-border-color/40'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full ${isOverridden ? 'bg-emerald-400 animate-pulse' : 'bg-text-secondary'}`}></span>
            {isOverridden ? 'Overridden' : 'Default'}
          </span>
        </div>

        {/* Script URL Input */}
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold text-text-secondary flex justify-between items-center">
            <span>Script / Bundle URL</span>
            <span className="text-[10px] text-text-muted font-mono truncate max-w-[250px]">
              {mfe.defaultScriptUrl}
            </span>
          </label>
          <input
            type="text"
            value={override?.scriptUrl || ''}
            onChange={(e) => onInputChange(mfe.key, 'scriptUrl', e.target.value)}
            placeholder={mfe.defaultScriptUrl}
            className="bg-bg-surface border border-border-color rounded-xl px-4 py-2.5 text-xs md:text-sm text-text-primary w-full outline-none focus:border-primary-accent transition-colors"
          />
        </div>

        {/* CSS URL Input (If applicable) */}
        {mfe.defaultCssUrl && (
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-text-secondary flex justify-between items-center">
              <span>Stylesheet URL (Optional)</span>
              <span className="text-[10px] text-text-muted font-mono truncate max-w-[250px]">
                {mfe.defaultCssUrl}
              </span>
            </label>
            <input
              type="text"
              value={override?.cssUrl || ''}
              onChange={(e) => onInputChange(mfe.key, 'cssUrl', e.target.value)}
              placeholder={mfe.defaultCssUrl}
              className="bg-bg-surface border border-border-color rounded-xl px-4 py-2.5 text-xs md:text-sm text-text-primary w-full outline-none focus:border-primary-accent transition-colors"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OverridesTab;
